import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { testimonials, platformStats, contactInfo } from '@/mocks/data';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9sq02tlbrt1qcgaj810';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // non-JSON response
      }

      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || '提交失败，请稍后再试。';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description="了解欧美一区的品牌故事与发展历程。欧美一区汇聚海量优质欧美影视、音乐与文化内容，为用户提供全面的资源导航与推荐服务。"
        keywords="欧美一区,欧美一区关于我们,欧美文化内容平台,欧美影视资源平台介绍"
        canonicalPath="/about"
        ogType="website"
        breadcrumbs={[
          { label: '首页', href: '/' },
          { label: '关于我们', href: '/about' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于欧美一区',
          description: '欧美一区精选内容导航平台品牌介绍',
          url: 'https://www.oumei1.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '欧美一区精选内容导航平台',
            url: 'https://www.oumei1.com/',
          },
          mainEntity: {
            '@type': 'Organization',
            name: '欧美一区',
            url: 'https://www.oumei1.com/',
            contactPoint: {
              '@type': 'ContactPoint',
              email: contactInfo.email,
              telephone: contactInfo.phone,
              contactType: 'customer service',
            },
          },
        }}
      />

      <main>
        <Navbar />

        <div className="pt-16 md:pt-18 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              关于欧美一区
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              致力于打造最专业的欧美文化内容导航平台，让每一位用户都能在这里发现优质的欧美一区影视、音乐与文化内容。
            </p>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12">
              {platformStats.map((stat) => (
                <div key={stat.label} className="text-center bg-white rounded-lg p-6 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-start">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
                  <p>
                    欧美一区创立于2020年，由一群热爱欧美文化的媒体人和技术专家共同发起。我们深知国内用户对优质欧美影视、音乐和文化内容的渴求，因此决心打造一个专业、全面、高品质的欧美一区内容导航平台。
                  </p>
                  <p>
                    经过数年的深耕与发展，欧美一区已经发展成为涵盖影视、音乐、综艺、纪录片、时尚、体育六大频道的综合性内容导航平台。我们坚持每日更新、精心筛选，确保每一位访问欧美一区的用户都能获取到最新、最优质的欧美一区内容推荐。
                  </p>
                  <p>
                    我们的使命是让更多中国用户便捷地了解和接触欧美优秀文化作品，通过欧美一区的桥梁作用，促进中外文化的交流与理解。我们相信，优质的欧美一区内容能够开阔视野、丰富生活体验。
                  </p>
                  <p>
                    欧美一区的编辑团队由资深影评人、音乐媒体人、时尚编辑和体育评论员组成，每一位成员都在自己的领域拥有丰富的经验和独到的见解。正是这种专业精神，让欧美一区成为了数百万用户信赖的欧美一区内容导航平台。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  核心优势
                </h2>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-star-line', title: '精选品质内容', desc: '欧美一区编辑团队严格把关，只推荐经过验证的高品质欧美一区影视、音乐和文化内容。' },
                    { icon: 'ri-flashlight-line', title: '实时更新追踪', desc: '欧美一区保持每日更新，确保您第一时间获取欧美一区最新影视上映、音乐发布和文化动态。' },
                    { icon: 'ri-layout-grid-line', title: '分类清晰易用', desc: '欧美一区六大频道覆盖主流内容类型，精准的标签体系和智能推荐让您轻松找到感兴趣的内容。' },
                    { icon: 'ri-user-heart-line', title: '用户至上体验', desc: '欧美一区持续优化产品体验，每一位用户的需求和反馈都是我们前进的方向。' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-background-200/70">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">来自欧美一区用户的真实评价</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, index) => (
                <div key={index} className="bg-white rounded-lg p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading font-semibold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                如果您有任何问题或建议，欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mb-10">
              <div className="bg-white rounded-lg p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-xs text-foreground-600">{contactInfo.email}</p>
              </div>
              <div className="bg-white rounded-lg p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-phone-line text-lg text-primary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                <p className="text-xs text-foreground-600">{contactInfo.phone}</p>
              </div>
              <div className="bg-white rounded-lg p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-map-pin-line text-lg text-primary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                <p className="text-xs text-foreground-600">{contactInfo.address}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 md:p-8 border border-background-200/70">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">
                发送消息
              </h3>

              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-primary-600"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-900 mb-1">消息已成功发送！</p>
                  <p className="text-xs text-foreground-500">感谢您的留言，我们会在24小时内回复您。</p>
                  <button
                    className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                    onClick={() => setFormStatus('idle')}
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="about-contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      消息内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（限500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">限500字以内</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                      <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}